import { Fragment } from "react";
import PropTypes from "prop-types";
import { usePathname, useSearchParams } from "next/navigation";

import classNames from "classnames";

import { getUrlToRedirect } from "@/src/utils/url";
import { numberFormatter } from "helpers/number-formatter";

import styles from "./styles.module.css";

const CategoryFilter = ({
  criteria,
  categoryTree,
  listingCountItems,
  totalCount,
  screenType,
  isLoadingListingCategoryCount,
}) => {
  const { tradeType, category, estateTypes } = criteria?.category || {};
  const estateTypesSlugs = estateTypes?.map((estateTypeItem) => estateTypeItem?.slug) || [];
  const totalCountResult = totalCount !== 0 ? totalCount : null;
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const adCountClass = classNames({
    [styles.adCount]: true,
    [styles.adCountLoading]: isLoadingListingCategoryCount,
  });

  const withoutProjectsCategoryTree = categoryTree.filter(
    (categoryItem) => categoryItem.url !== "satilik-konut/projeler" && categoryItem.url !== "emlak-ofisleri"
  );

  const handleClickCategory = (categoryItemUrl) => {
    const url = getUrlToRedirect(categoryItemUrl, screenType);
    return url.startsWith("/") ? decodeURIComponent(url) : `/${decodeURIComponent(url)}`;
  };

  const buildSlugAndQuery = (pathname, searchParams) => {
    const EXCLUDED_SLUGS = ["projeler", "emlakcidan", "sahibinden", "muteahhitten", "bankadan"];
    const LOCATION_KEYS = ["ilce", "semt", "mahalle"];
    const buckets = { ilce: new Set(), semt: new Set(), mahalle: new Set() };

    const pathParts = pathname.split("/").filter(Boolean);
    const secondSlug = pathParts[1];
    const slugPart = secondSlug && !EXCLUDED_SLUGS.includes(secondSlug.toLowerCase()) ? `/${secondSlug}` : "";

    const rawFilterParams =
      typeof searchParams.getAll === "function"
        ? searchParams.getAll("filtreler")
        : searchParams.get?.("filtreler")
        ? [searchParams.get("filtreler")]
        : [];

    rawFilterParams.forEach((raw) => {
      if (!raw) return;
      raw
        .split(";")
        .map((section) => section.trim())
        .filter(Boolean)
        .forEach((section) => {
          const [keyRaw, valuesStr = ""] = section.split("=");
          const key = keyRaw?.trim().toLowerCase();

          if (!LOCATION_KEYS.includes(key)) return;

          valuesStr
            .split(",")
            .map((val) => val.trim())
            .filter(Boolean)
            .forEach((value) => buckets[key].add(value));
        });
    });

    LOCATION_KEYS.forEach((key) => {
      const values = typeof searchParams.getAll === "function" ? searchParams.getAll(key) : [];
      values.forEach((raw) => {
        raw
          .split(",")
          .map((val) => val.trim())
          .filter(Boolean)
          .forEach((value) => buckets[key].add(value));
      });
    });

    const filterParts = LOCATION_KEYS.map((key) => {
      const values = Array.from(buckets[key]);
      return values.length ? `${key}=${values.join(",")}` : null;
    }).filter(Boolean);

    const viewType = searchParams.get("view_type");

    const queryParams = [];
    if (filterParts.length > 0) queryParams.push(`filtreler=${filterParts.join("&")}`);
    if (viewType) queryParams.push(`view_type=${viewType}`);

    const queryPart = queryParams.length > 0 ? `?${queryParams.join("&")}` : "";

    return `${slugPart}${queryPart}`;
  };

  return (
    <div>
      <ul className={styles.wrapper}>
        {withoutProjectsCategoryTree.map((categoryTreeItem) => (
          <li className={styles.subMenu1} key={categoryTreeItem.url}>
            <a
              href={`${handleClickCategory(`${categoryTreeItem.url}-konut`)}${buildSlugAndQuery(
                pathname,
                searchParams
              )}`}
              title={categoryTreeItem?.name}
            >
              <span>{categoryTreeItem.name}</span>
            </a>
            {categoryTreeItem.child && (
              <ul className={styles.parentUlSubMenu}>
                {categoryTreeItem.child.map((categoryTreeItemChild, index) => {
                  const isActiveSubMenu =
                    estateTypesSlugs.length === 0 &&
                    categoryTreeItemChild?.tradeTypeSlug === tradeType?.slug &&
                    categoryTreeItemChild?.categorySlug === category?.slug;

                  const totalCountResultVisibility =
                    isActiveSubMenu || (criteria?.projectSearch && criteria.params.ownerType === "PROJECTS");

                  const saleIncludedTradeTypeSlugs = ["devren", "kat-karsiligi"];
                  const rentIncludedTradeTypeSlugs = ["gunluk-kiralik"];
                  const isVisible =
                    categoryTreeItemChild?.tradeTypeSlug === tradeType?.slug ||
                    (saleIncludedTradeTypeSlugs.includes(categoryTreeItemChild?.tradeTypeSlug) &&
                      tradeType?.slug === "satilik") ||
                    (rentIncludedTradeTypeSlugs.includes(categoryTreeItemChild?.tradeTypeSlug) &&
                      tradeType?.slug === "kiralik") ||
                    (categoryTreeItemChild?.tradeTypeSlug === "satilik" &&
                      saleIncludedTradeTypeSlugs.includes(tradeType?.slug)) ||
                    (categoryTreeItemChild?.tradeTypeSlug === "kiralik" &&
                      rentIncludedTradeTypeSlugs.includes(tradeType?.slug));

                  return (
                    <Fragment key={index}>
                      {isVisible && (
                        <li className={styles.subMenu2}>
                          <a
                            href={`${handleClickCategory(categoryTreeItemChild?.url)}${buildSlugAndQuery(
                              pathname,
                              searchParams
                            )}`}
                            title={`${categoryTreeItem.name} ${categoryTreeItemChild?.name}`}
                          >
                            <span className={isActiveSubMenu ? styles.activeSubMenu : ""}>
                              {categoryTreeItemChild.name}
                              {totalCountResultVisibility && (
                                <span className={styles.adCount}>{numberFormatter(totalCountResult)}</span>
                              )}
                            </span>
                          </a>
                          {categoryTreeItemChild.child && (
                            <ul className={styles.ulSubMenu}>
                              {categoryTreeItemChild.child.map((categoryTreeItemChildOfChild, index) => {
                                const estateTypeCount = listingCountItems?.find(
                                  (categoryTreeItem) =>
                                    categoryTreeItem?.estateTypeId === categoryTreeItemChildOfChild?.estateTypeId
                                );

                                const isActiveChildSubMenu = estateTypesSlugs.includes(
                                  categoryTreeItemChildOfChild.estateTypeSlug
                                );

                                let isChildVisible;

                                if (categoryTreeItemChildOfChild?.url?.startsWith("gunluk-kiralik")) {
                                  isChildVisible = tradeType.slug === "gunluk-kiralik";
                                } else if (tradeType.slug === "devren") {
                                  isChildVisible = categoryTreeItemChildOfChild?.url?.startsWith("devren-");
                                } else if (tradeType.slug === "kat-karsiligi") {
                                  isChildVisible = categoryTreeItemChildOfChild?.url?.startsWith("kat-karsiligi-");
                                } else if (tradeType.slug === "gunluk-kiralik") {
                                  isChildVisible = categoryTreeItemChildOfChild?.url?.startsWith("gunluk-kiralik-");
                                } else if (
                                  category.slug === "arsa" &&
                                  categoryTreeItemChildOfChild?.url?.startsWith("kat-karsiligi-")
                                ) {
                                  isChildVisible = false;
                                } else if (
                                  category.slug === "isyeri" &&
                                  categoryTreeItemChildOfChild?.url?.startsWith("devren-")
                                ) {
                                  isChildVisible = false;
                                } else {
                                  isChildVisible = categoryTreeItemChildOfChild?.categorySlug === category?.slug;
                                }

                                return (
                                  <Fragment key={index}>
                                    {isChildVisible && (
                                      <li className={styles.subMenu2} key={categoryTreeItemChildOfChild.id}>
                                        <a
                                          href={`${handleClickCategory(
                                            categoryTreeItemChildOfChild?.url
                                          )}${buildSlugAndQuery(pathname, searchParams)}`}
                                          title={`${categoryTreeItem.name} ${categoryTreeItemChildOfChild?.name}`}
                                        >
                                          <span className={isActiveChildSubMenu ? styles.activeSubMenu : ""}>
                                            {categoryTreeItemChildOfChild.name}
                                            {estateTypeCount && (
                                              <span className={adCountClass}>
                                                {`(${numberFormatter(estateTypeCount.listingCount)})`}
                                              </span>
                                            )}
                                          </span>
                                        </a>
                                      </li>
                                    )}
                                  </Fragment>
                                );
                              })}
                            </ul>
                          )}
                        </li>
                      )}
                    </Fragment>
                  );
                })}
              </ul>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
};

CategoryFilter.propTypes = {
  categoryTree: PropTypes.array,
  criteria: PropTypes.object,
  listingCountItems: PropTypes.array,
  totalCount: PropTypes.number,
  screenType: PropTypes.string,
  isLoadingListingCategoryCount: PropTypes.bool,
};

export default CategoryFilter;
